import { useState } from "react";
import Constants from "expo-constants";
import { useRouter } from "expo-router";
import { Platform } from "react-native";
import {
  TICKET_LIMITS,
  TICKET_STATUS_LABELS,
  TICKET_TYPE_LABELS,
  TICKET_TYPE_OPTIONS,
  type TicketStatus,
  type TicketType,
} from "@brad-tipper/app-frontend/tickets";

import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  ListRow,
  ProtectedScreen,
  Row,
  SectionHeader,
  SegmentedControl,
  Stack,
  TextField,
} from "~/components/ui";
import { useCreateTicket, useTickets } from "~/features/support/tickets";

const statusTone = (status: TicketStatus) =>
  status === "resolved"
    ? "success"
    : status === "in_progress" || status === "planned"
      ? "warning"
      : status === "closed"
        ? "neutral"
        : "mint";

export default function SupportScreen() {
  const router = useRouter();
  const [filter, setFilter] = useState<"open" | "closed">("open");
  const [composing, setComposing] = useState(false);
  const [type, setType] = useState<TicketType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const tickets = useTickets(filter);
  const create = useCreateTicket();

  const valid = title.trim().length >= 3 && description.trim().length >= 10;

  async function submit() {
    if (!valid) return;
    const ticket = await create.mutateAsync({
      type,
      title: title.trim(),
      description: description.trim(),
      appVersion: Constants.expoConfig?.version,
      platform: Platform.OS,
    });
    setTitle("");
    setDescription("");
    setComposing(false);
    router.push(`/support/${ticket.id}` as never);
  }

  return (
    <ProtectedScreen
      narrow
      title="Support"
      subtitle="Report a bug, ask for a feature or leave feedback. Follow it here and add notes any time."
    >
      {composing ? (
        <Card gap="md">
          <SectionHeader title="New ticket" />
          <SegmentedControl
            accessibilityLabel="Ticket type"
            options={TICKET_TYPE_OPTIONS}
            value={type}
            onChange={setType}
          />
          <Stack gap="xs">
            <AppText size="xs" weight="medium">
              Title
            </AppText>
            <TextField
              accessibilityLabel="Title"
              value={title}
              onChangeText={setTitle}
              maxLength={TICKET_LIMITS.title}
              placeholder="A short summary"
            />
          </Stack>
          <Stack gap="xs">
            <AppText size="xs" weight="medium">
              Details
            </AppText>
            <TextField
              accessibilityLabel="Details"
              value={description}
              onChangeText={setDescription}
              maxLength={TICKET_LIMITS.description}
              multiline
              numberOfLines={6}
              textAlignVertical="top"
              placeholder={
                type === "bug"
                  ? "What happened, and what did you expect?"
                  : "Tell us more"
              }
            />
          </Stack>
          {create.error ? (
            <AppText tone="danger" size="sm">
              {create.error instanceof Error
                ? create.error.message
                : "Unable to send."}
            </AppText>
          ) : null}
          <Row>
            <Button
              disabled={!valid || create.isPending}
              onPress={() => void submit()}
              align="start"
            >
              <ButtonText>{create.isPending ? "Sending…" : "Send"}</ButtonText>
            </Button>
            <Button
              variant="outline"
              align="start"
              onPress={() => setComposing(false)}
            >
              <ButtonText>Cancel</ButtonText>
            </Button>
          </Row>
        </Card>
      ) : (
        <Button align="start" onPress={() => setComposing(true)}>
          <ButtonText>New ticket</ButtonText>
        </Button>
      )}

      <SegmentedControl
        accessibilityLabel="Ticket filter"
        options={[
          { label: "Open", value: "open" as const },
          { label: "Closed", value: "closed" as const },
        ]}
        value={filter}
        onChange={setFilter}
      />

      {tickets.isLoading ? (
        <AppText tone="muted">Loading…</AppText>
      ) : tickets.error ? (
        <AppText tone="danger">Unable to load your tickets.</AppText>
      ) : tickets.data && tickets.data.length > 0 ? (
        <Card>
          {tickets.data.map((ticket, index) => (
            <ListRow
              key={ticket.id}
              first={index === 0}
              title={`#${ticket.number} ${ticket.title}`}
              description={`${TICKET_TYPE_LABELS[ticket.type]} · updated ${new Date(ticket.updatedAt).toLocaleDateString()}`}
              trailing={
                <Badge tone={statusTone(ticket.status)}>
                  {TICKET_STATUS_LABELS[ticket.status]}
                </Badge>
              }
              onPress={() => router.push(`/support/${ticket.id}` as never)}
              accessibilityLabel={`Open ticket ${ticket.number}`}
            />
          ))}
        </Card>
      ) : (
        <EmptyState
          title={filter === "open" ? "No open tickets" : "No closed tickets"}
          description="Anything you report shows up here."
        />
      )}
    </ProtectedScreen>
  );
}
